import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSession, type Session } from '../lib/session';
import { ORG_PROFILE } from '../lib/fixtures';
import { apiFetch } from '../lib/api';
import { useMembers } from '../lib/members';
import type { Member, OrgProfile } from '../lib/contract';
import { CSIRTS } from '../lib/cra';
import { Badge, Btn, Card, Field, PageHead, Tabs } from '../components/ui/primitives';
import { CountrySelector } from '../components/ui/CountrySelector';
import { UserCombo } from '../components/ui/UserCombo';

type TabKey = 'org' | 'me' | 'users' | 'keys' | 'hooks' | 'alerts';

function CsirtCombo({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [q, setQ] = useState(value);
  const [open, setOpen] = useState(false);
  // Hydrate when the stored profile arrives (live) without clobbering active typing.
  useEffect(() => { setQ(value); }, [value]);
  const opts = useMemo(() => CSIRTS.filter((c) => (c.n + ' ' + c.c).toLowerCase().includes(q.toLowerCase().trim())), [q]);
  const pick = (v: string) => { setQ(v); onChange(v); };
  return (
    <div className="combo">
      <input className="input combo-input" value={q} autoComplete="off" placeholder="Search national CSIRT…" data-testid="csirt-input"
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)} onChange={(e) => { pick(e.target.value); setOpen(true); }} />
      <span className="combo-caret">▾</span>
      {open && (
        <div className="combo-list" style={{ display: 'block' }}>
          {opts.length === 0 ? <div className="combo-empty">No CSIRT matches that search</div>
            : opts.map((c) => <div key={c.c} className="combo-opt" onMouseDown={(e) => { e.preventDefault(); pick(`${c.n} (${c.c})`); setOpen(false); }}><span>{c.n}</span><span className="cc">{c.c}</span></div>)}
        </div>
      )}
    </div>
  );
}

// ─── Integrations & alert channels (F5 · F9 · F13) ──────────────────────────────
// Real CRUD for three previously-dead Settings tabs, wired to the existing
// admin-gated API routes (/api/keys, /api/webhooks, /api/channels). In fixtures
// mode the tabs run off a tiny in-memory demo dataset so /preview stays alive with
// zero network; with VITE_USE_FIXTURES=false the same UI hits the real API.

type ApiKeyScope = 'read' | 'uploads';
interface ApiKeyRow {
  id: string; name: string; prefix: string; scopes: ApiKeyScope[];
  createdAt: string; lastUsedAt?: string | null; revokedAt?: string | null;
}
interface MintedKey { id: string; key: string; prefix: string; scopes: ApiKeyScope[]; }

interface WebhookRow { id: string; url: string; events: string[]; enabled: boolean; createdAt: string; }
interface MintedWebhook { id: string; secret: string; url: string; events: string[]; }

type ChannelKind = 'slack' | 'teams' | 'google_chat' | 'telegram' | 'webhook' | 'email';
interface ChannelRow { id: string; kind: ChannelKind; name: string; classes: string[] | null; enabled: boolean; createdAt: string; }

const KEY_SCOPES: ReadonlyArray<{ id: ApiKeyScope; hint: string }> = [
  { id: 'read', hint: 'read findings, products & Art.14' },
  { id: 'uploads', hint: 'push SBOMs from CI' },
];

// Event names the backend actually raises (fanoutWebhooksForOrg call sites). The
// route accepts any non-empty string ≤64 chars; these are the meaningful ones.
const WEBHOOK_EVENTS = [
  'art14.raised', 'art14.notified', 'finding.opened', 'finding.resolved',
  'sbom.uploaded', 'device.lifecycle', 'plan.changed',
] as const;

const CHANNEL_KINDS: ReadonlyArray<{ id: ChannelKind; label: string }> = [
  { id: 'slack', label: 'Slack' }, { id: 'teams', label: 'Microsoft Teams' },
  { id: 'google_chat', label: 'Google Chat' }, { id: 'telegram', label: 'Telegram' },
  { id: 'webhook', label: 'Generic webhook' }, { id: 'email', label: 'Email distribution list' },
];
const CHANNEL_ICON: Record<ChannelKind, string> = {
  slack: '💬', teams: '👥', google_chat: '💬', telegram: '✈️', webhook: '🔗', email: '✉️',
};

const DEMO_KEYS: ApiKeyRow[] = [
  { id: 'k-ci', name: 'GitHub Actions · CI', prefix: 'bpk_7f3a2c', scopes: ['read', 'uploads'], createdAt: '2026-06-02T09:14:00Z', lastUsedAt: '2026-07-17T22:03:00Z' },
  { id: 'k-grafana', name: 'Grafana (read-only)', prefix: 'bpk_9d1e88', scopes: ['read'], createdAt: '2026-05-20T11:40:00Z', lastUsedAt: null },
];
const DEMO_HOOKS: WebhookRow[] = [
  { id: 'wh-cms', url: 'https://hooks.acme-ind.example/bicport', events: ['art14.raised', 'art14.notified'], enabled: true, createdAt: '2026-06-10T08:00:00Z' },
];
const DEMO_CHANNELS: ChannelRow[] = [
  { id: 'ch-slack', kind: 'slack', name: 'SecOps Slack', classes: null, enabled: true, createdAt: '2026-06-01T08:00:00Z' },
  { id: 'ch-mail', kind: 'email', name: 'compliance@acme (DL)', classes: ['art14_trigger', 'deadline_reminder'], enabled: true, createdAt: '2026-06-01T08:00:00Z' },
];

function fmtDate(iso?: string | null): string { return iso ? iso.slice(0, 10) : '—'; }
function randToken(len = 28): string {
  const a = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let s = ''; for (let i = 0; i < len; i++) s += a.charAt(Math.floor(Math.random() * a.length));
  return s;
}
/** Turn a failed Response into a friendly, inline message (403 → role hint). */
async function apiError(r: Response): Promise<string> {
  if (r.status === 403) return 'This action requires Admin/Manager.';
  if (r.status === 401) return 'Your session expired — please sign in again.';
  try {
    const b = (await r.json()) as { error?: unknown };
    if (typeof b.error === 'string' && b.error) return b.error;
  } catch { /* no JSON body */ }
  return `Request failed (${r.status}).`;
}

function ErrLine({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return <p className="hint" role="alert" style={{ color: 'var(--critical)', marginTop: 10 }}>{msg}</p>;
}

function CopyBtn({ value }: { value: string }) {
  const [ok, setOk] = useState(false);
  return (
    <Btn sm onClick={() => {
      const c = navigator.clipboard;
      if (c) void c.writeText(value).then(() => { setOk(true); setTimeout(() => setOk(false), 1400); }, () => undefined);
    }}>{ok ? 'Copied ✓' : 'Copy'}</Btn>
  );
}

/** One-time secret reveal card — the ONLY place a plaintext key/secret is shown. */
function SecretReveal({ title, value, warning, onDone, extra }:
  { title: string; value: string; warning: string; onDone: () => void; extra?: ReactNode }) {
  return (
    <Card style={{ padding: 16, marginTop: 14, borderColor: 'color-mix(in srgb,var(--medium) 45%,var(--border))', background: 'color-mix(in srgb,var(--medium) 8%,var(--surface))' }}>
      <div className="section-head"><h3>{title}</h3><Badge kind="warn">shown once</Badge></div>
      <p className="hint" style={{ margin: '6px 0 10px' }}>{warning}</p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code className="mono" data-testid="secret-value" style={{ flex: 1, minWidth: 220, wordBreak: 'break-all', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px' }}>{value}</code>
        <CopyBtn value={value} />
      </div>
      {extra}
      <div className="form-actions" style={{ marginTop: 14 }}><Btn sm variant="primary" onClick={onDone}>Done</Btn></div>
    </Card>
  );
}

/** Two-step inline delete (no modal). */
function ConfirmDelete({ label, onConfirm }: { label: string; onConfirm: () => void }) {
  const [armed, setArmed] = useState(false);
  if (!armed) return <Btn sm variant="danger" onClick={() => setArmed(true)}>{label}</Btn>;
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <Btn sm variant="danger" onClick={() => { setArmed(false); onConfirm(); }}>Confirm</Btn>
      <Btn sm onClick={() => setArmed(false)}>Cancel</Btn>
    </span>
  );
}

// ─── API keys tab (F9 / CI-01) ──────────────────────────────────────────────────
function KeysTab({ session, useFixtures }: { session: Session; useFixtures: boolean }) {
  const [keys, setKeys] = useState<ApiKeyRow[] | null>(useFixtures ? DEMO_KEYS : null);
  const [loading, setLoading] = useState(!useFixtures);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>(['read']);
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<MintedKey | null>(null);

  useEffect(() => {
    if (useFixtures) return;
    let live = true; setLoading(true); setError(null);
    void apiFetch(session, '/api/keys')
      .then(async (r) => { if (!r.ok) throw await apiError(r); return (await r.json()) as { keys: ApiKeyRow[] }; })
      .then((b) => { if (live) { setKeys(b.keys); setLoading(false); } })
      .catch((e: unknown) => { if (live) { setError(typeof e === 'string' ? e : 'Could not load keys.'); setLoading(false); } });
    return () => { live = false; };
  }, [session, useFixtures]);

  const toggle = (s: ApiKeyScope) => setScopes((c) => (c.includes(s) ? c.filter((x) => x !== s) : [...c, s]));
  const reset = () => { setAdding(false); setName(''); setScopes(['read']); };

  async function create() {
    setBusy(true); setError(null);
    const cleanName = name.trim() || 'Untitled key';
    if (useFixtures) {
      const prefix = 'bpk_' + randToken(6).toLowerCase();
      const id = 'k-' + randToken(6).toLowerCase();
      setKeys((k) => [{ id, name: cleanName, prefix, scopes, createdAt: new Date().toISOString(), lastUsedAt: null }, ...(k ?? [])]);
      setMinted({ id, key: `${prefix}_${randToken(32)}`, prefix, scopes });
      reset(); setBusy(false); return;
    }
    const r = await apiFetch(session, '/api/keys', { method: 'POST', body: JSON.stringify({ name: cleanName, scopes }) });
    if (!r.ok) { setError(await apiError(r)); setBusy(false); return; }
    const m = (await r.json()) as MintedKey;
    setMinted(m);
    setKeys((k) => [{ id: m.id, name: cleanName, prefix: m.prefix, scopes: m.scopes, createdAt: new Date().toISOString(), lastUsedAt: null }, ...(k ?? [])]);
    reset(); setBusy(false);
  }

  async function revoke(id: string) {
    setError(null);
    if (!useFixtures) {
      const r = await apiFetch(session, `/api/keys/${id}`, { method: 'DELETE' });
      if (!r.ok) { setError(await apiError(r)); return; }
    }
    setKeys((k) => (k ?? []).map((x) => (x.id === id ? { ...x, revokedAt: new Date().toISOString() } : x)));
  }

  return (
    <Card style={{ padding: 20, maxWidth: 720 }}>
      <div className="section-head">
        <h3>API keys</h3>
        {!adding && <Btn sm variant="primary" onClick={() => { setAdding(true); setMinted(null); }}>＋ New key</Btn>}
      </div>
      <p className="hint" style={{ marginTop: 8 }}>CI/GitHub push SBOMs and read findings with a scoped key. The secret is revealed only at creation.</p>

      {minted && (
        <SecretReveal title="Copy your new API key" value={minted.key}
          warning="This is the only time the full key is shown. Store it in your CI secret manager now — it cannot be retrieved later."
          onDone={() => setMinted(null)}
          extra={<p className="hint" style={{ marginTop: 8 }}>Prefix <span className="mono">{minted.prefix}</span> · scopes <span className="mono">{minted.scopes.join(' + ') || 'none'}</span></p>} />
      )}

      {adding && (
        <Card style={{ padding: 16, marginTop: 14, background: 'var(--surface-tint)' }}>
          <div className="form" style={{ gap: 14 }}>
            <Field label="Key name" hint="how you'll recognise it — e.g. “GitHub Actions · release”">
              <input className="input" autoFocus value={name} placeholder="Key name" onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Scopes" hint="least privilege — grant only what the integration needs">
              <div>{KEY_SCOPES.map((s) => (
                <label className="ck" key={s.id}><input type="checkbox" checked={scopes.includes(s.id)} onChange={() => toggle(s.id)} /> <span><span className="mono">{s.id}</span> — {s.hint}</span></label>
              ))}</div>
            </Field>
            <div className="form-actions">
              <Btn sm onClick={() => { reset(); setError(null); }}>Cancel</Btn>
              <Btn sm variant="primary" disabled={busy || scopes.length === 0} onClick={() => void create()}>{busy ? 'Creating…' : 'Create key'}</Btn>
            </div>
          </div>
        </Card>
      )}

      <ErrLine msg={error} />

      <div style={{ marginTop: 16 }}>
        {loading ? <p className="hint">Loading keys…</p>
          : keys && keys.length > 0 ? (
            <div className="doc-list">{keys.map((k) => (
              <div className="doc" key={k.id}>
                <div className="doc-ic">🔑</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="doc-n">{k.name} {k.revokedAt && <Badge kind="crit">revoked</Badge>}</div>
                  <div className="doc-m"><span className="mono">{k.prefix}…</span> · {k.scopes.join(' + ') || 'no scopes'} · created {fmtDate(k.createdAt)} · {k.lastUsedAt ? `last used ${fmtDate(k.lastUsedAt)}` : 'never used'}</div>
                </div>
                {!k.revokedAt && <ConfirmDelete label="Revoke" onConfirm={() => void revoke(k.id)} />}
              </div>
            ))}</div>
          ) : <p className="hint">No API keys yet. Create one to push SBOMs from CI.</p>}
      </div>
    </Card>
  );
}

// ─── Webhooks tab (F13) ─────────────────────────────────────────────────────────
function HooksTab({ session, useFixtures }: { session: Session; useFixtures: boolean }) {
  const [hooks, setHooks] = useState<WebhookRow[] | null>(useFixtures ? DEMO_HOOKS : null);
  const [loading, setLoading] = useState(!useFixtures);
  const [planLocked, setPlanLocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<MintedWebhook | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  useEffect(() => {
    if (useFixtures) return;
    let live = true; setLoading(true); setError(null);
    void apiFetch(session, '/api/webhooks')
      .then(async (r) => {
        if (r.status === 404) { if (live) { setPlanLocked(true); setLoading(false); } return null; }
        if (!r.ok) throw await apiError(r);
        return (await r.json()) as { subscriptions: WebhookRow[] };
      })
      .then((b) => { if (live && b) { setHooks(b.subscriptions); setLoading(false); } })
      .catch((e: unknown) => { if (live) { setError(typeof e === 'string' ? e : 'Could not load webhooks.'); setLoading(false); } });
    return () => { live = false; };
  }, [session, useFixtures]);

  const toggleEvent = (ev: string) => setEvents((c) => (c.includes(ev) ? c.filter((x) => x !== ev) : [...c, ev]));
  const reset = () => { setAdding(false); setUrl(''); setEvents([]); };

  async function create() {
    setBusy(true); setError(null);
    const cleanUrl = url.trim();
    if (useFixtures) {
      const id = 'wh-' + randToken(6).toLowerCase();
      setHooks((h) => [{ id, url: cleanUrl, events, enabled: true, createdAt: new Date().toISOString() }, ...(h ?? [])]);
      setMinted({ id, secret: 'whsec_' + randToken(40), url: cleanUrl, events });
      reset(); setBusy(false); return;
    }
    const r = await apiFetch(session, '/api/webhooks', { method: 'POST', body: JSON.stringify({ url: cleanUrl, events }) });
    if (r.status === 404) { setPlanLocked(true); setBusy(false); return; }
    if (!r.ok) { setError(await apiError(r)); setBusy(false); return; }
    const m = (await r.json()) as MintedWebhook;
    setMinted(m);
    setHooks((h) => [{ id: m.id, url: m.url, events: m.events, enabled: true, createdAt: new Date().toISOString() }, ...(h ?? [])]);
    reset(); setBusy(false);
  }

  async function remove(id: string) {
    setError(null);
    if (!useFixtures) {
      const r = await apiFetch(session, `/api/webhooks?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!r.ok) { setError(await apiError(r)); return; }
    }
    setHooks((h) => (h ?? []).filter((x) => x.id !== id));
  }

  async function sendTest(m: MintedWebhook) {
    setTestMsg('Sending test delivery…');
    if (useFixtures) { setTestMsg('Test delivery sent ✓ (demo)'); return; }
    const r = await apiFetch(session, '/api/webhooks/test', { method: 'POST', body: JSON.stringify({ url: m.url, secret: m.secret }) });
    const b = (await r.json().catch(() => ({}))) as { delivered?: boolean; ok?: boolean; status?: number; error?: string };
    setTestMsg(b.delivered || b.ok ? `Test delivery accepted ✓${b.status ? ` (HTTP ${b.status})` : ''}` : `Test delivery failed: ${b.error ?? 'no response'}`);
  }

  if (planLocked) {
    return (
      <Card style={{ padding: 20, maxWidth: 720 }}>
        <div className="section-head"><h3>Webhooks</h3></div>
        <div className="ack-warn" style={{ marginTop: 12 }}>Outbound webhooks aren't included on your current plan. Add the <b>integrations</b> entitlement to notify your systems (CMS, SIEM, ticketing) on Art.14 and finding events.</div>
      </Card>
    );
  }

  return (
    <Card style={{ padding: 20, maxWidth: 720 }}>
      <div className="section-head">
        <h3>Webhooks</h3>
        {!adding && <Btn sm variant="primary" onClick={() => { setAdding(true); setMinted(null); setTestMsg(null); }}>＋ Add webhook</Btn>}
      </div>
      <p className="hint" style={{ marginTop: 8 }}>Signed, HTTPS-only deliveries to your systems. Payloads carry ids, counts and outcomes only — never SBOMs or personal data.</p>

      {minted && (
        <SecretReveal title="Copy your signing secret" value={minted.secret}
          warning="Verify the HMAC signature on every delivery with this secret. It is shown once and cannot be retrieved later."
          onDone={() => { setMinted(null); setTestMsg(null); }}
          extra={<div style={{ marginTop: 10 }}>
            <Btn sm onClick={() => void sendTest(minted)}>Send test delivery</Btn>
            {testMsg && <span className="hint" style={{ marginLeft: 10 }}>{testMsg}</span>}
          </div>} />
      )}

      {adding && (
        <Card style={{ padding: 16, marginTop: 14, background: 'var(--surface-tint)' }}>
          <div className="form" style={{ gap: 14 }}>
            <Field label="Payload URL" hint="must be https">
              <input className="input" autoFocus value={url} placeholder="https://…" onChange={(e) => setUrl(e.target.value)} />
            </Field>
            <Field label="Events" hint="deliver only the events you subscribe to">
              <div className="filters">{WEBHOOK_EVENTS.map((ev) => (
                <span key={ev} className={`chip${events.includes(ev) ? ' on' : ''}`} onClick={() => toggleEvent(ev)}>{ev}</span>
              ))}</div>
            </Field>
            <div className="form-actions">
              <Btn sm onClick={() => { reset(); setError(null); }}>Cancel</Btn>
              <Btn sm variant="primary" disabled={busy || events.length === 0 || !/^https:\/\//i.test(url.trim())} onClick={() => void create()}>{busy ? 'Creating…' : 'Create webhook'}</Btn>
            </div>
          </div>
        </Card>
      )}

      <ErrLine msg={error} />

      <div style={{ marginTop: 16 }}>
        {loading ? <p className="hint">Loading webhooks…</p>
          : hooks && hooks.length > 0 ? (
            <div className="doc-list">{hooks.map((h) => (
              <div className="doc" key={h.id}>
                <div className="doc-ic">🔗</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="doc-n" style={{ wordBreak: 'break-all' }}>{h.url}</div>
                  <div className="doc-m">{h.events.join(', ')} · {h.enabled ? 'enabled' : 'disabled'} · created {fmtDate(h.createdAt)}</div>
                </div>
                <ConfirmDelete label="Delete" onConfirm={() => void remove(h.id)} />
              </div>
            ))}</div>
          ) : <p className="hint">No webhooks yet.</p>}
      </div>
    </Card>
  );
}

// ─── Alerts / notification channels tab (F5) ────────────────────────────────────
function AlertsTab({ session, useFixtures, isManager }: { session: Session; useFixtures: boolean; isManager: boolean }) {
  const [channels, setChannels] = useState<ChannelRow[] | null>(useFixtures ? DEMO_CHANNELS : null);
  const [loading, setLoading] = useState(!useFixtures && isManager);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<ChannelKind>('slack');
  const [name, setName] = useState('');
  const [cfg, setCfg] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [testId, setTestId] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  useEffect(() => {
    if (useFixtures || !isManager) return;
    let live = true; setLoading(true); setError(null);
    void apiFetch(session, '/api/channels')
      .then(async (r) => { if (!r.ok) throw await apiError(r); return (await r.json()) as { channels: ChannelRow[] }; })
      .then((b) => { if (live) { setChannels(b.channels); setLoading(false); } })
      .catch((e: unknown) => { if (live) { setError(typeof e === 'string' ? e : 'Could not load channels.'); setLoading(false); } });
    return () => { live = false; };
  }, [session, useFixtures, isManager]);

  const setField = (k: string, v: string) => setCfg((c) => ({ ...c, [k]: v }));
  const reset = () => { setAdding(false); setName(''); setCfg({}); setKind('slack'); };

  /** Build the per-kind config the route expects, or a validation message. */
  function buildConfig(): Record<string, unknown> | string {
    if (kind === 'slack' || kind === 'teams' || kind === 'google_chat') {
      const webhookUrl = (cfg.webhookUrl ?? '').trim();
      if (!/^https:\/\//i.test(webhookUrl)) return 'Enter an https incoming-webhook URL.';
      return { webhookUrl };
    }
    if (kind === 'webhook') {
      const u = (cfg.url ?? '').trim();
      if (!/^https:\/\//i.test(u)) return 'Enter an https endpoint URL.';
      return { url: u };
    }
    if (kind === 'telegram') {
      const botToken = (cfg.botToken ?? '').trim();
      const chatId = (cfg.chatId ?? '').trim();
      if (!botToken || !chatId) return 'Enter the bot token and chat id.';
      return { botToken, chatId };
    }
    const groupAddress = (cfg.groupAddress ?? '').trim();
    if (!groupAddress.includes('@')) return 'Enter a distribution email address.';
    return { groupAddress };
  }

  async function create() {
    if (!name.trim()) { setError('Give the channel a name.'); return; }
    const config = buildConfig();
    if (typeof config === 'string') { setError(config); return; }
    setBusy(true); setError(null);
    if (useFixtures) {
      const id = 'ch-' + randToken(6).toLowerCase();
      setChannels((c) => [{ id, kind, name: name.trim(), classes: null, enabled: true, createdAt: new Date().toISOString() }, ...(c ?? [])]);
      reset(); setBusy(false); return;
    }
    const r = await apiFetch(session, '/api/channels', { method: 'POST', body: JSON.stringify({ kind, name: name.trim(), config }) });
    if (!r.ok) { setError(await apiError(r)); setBusy(false); return; }
    const c = (await r.json()) as { id: string; kind: ChannelKind; name: string; classes: string[] | null };
    setChannels((cur) => [{ id: c.id, kind: c.kind, name: c.name, classes: c.classes, enabled: true, createdAt: new Date().toISOString() }, ...(cur ?? [])]);
    reset(); setBusy(false);
  }

  async function remove(id: string) {
    setError(null);
    if (!useFixtures) {
      const r = await apiFetch(session, `/api/channels/${id}`, { method: 'DELETE' });
      if (!r.ok) { setError(await apiError(r)); return; }
    }
    setChannels((c) => (c ?? []).filter((x) => x.id !== id));
  }

  async function test(id: string) {
    setTestId(id); setTestMsg('Sending test alert…');
    if (useFixtures) { setTestMsg('Test alert delivered ✓ (demo)'); return; }
    const r = await apiFetch(session, `/api/channels/${id}/test`, { method: 'POST' });
    const b = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string };
    setTestMsg(b.ok ? 'Test alert delivered ✓' : `Test alert failed: ${b.error ?? `HTTP ${r.status}`}`);
  }

  return (
    <Card style={{ padding: 20, maxWidth: 720 }}>
      <div className="section-head">
        <h3>Organisation alert channels</h3>
        {isManager ? (!adding && <Btn sm variant="primary" onClick={() => setAdding(true)}>＋ Add channel</Btn>) : <Badge kind="out">Admin/Manager</Badge>}
      </div>
      <p className="hint" style={{ marginTop: 8 }}>Where KEV / Art.14 and finding alerts fan out. One event reaches every enabled channel.</p>
      {!isManager && <div className="ack-warn" style={{ marginTop: 10 }}>Managing organisation alert channels requires Admin/Manager.</div>}

      {isManager && adding && (
        <Card style={{ padding: 16, marginTop: 14, background: 'var(--surface-tint)' }}>
          <div className="form" style={{ gap: 14 }}>
            <div className="grid2">
              <Field label="Channel type">
                <select className="select" value={kind} onChange={(e) => { setKind(e.target.value as ChannelKind); setCfg({}); }}>
                  {CHANNEL_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
              </Field>
              <Field label="Name" hint="how it appears in the list">
                <input className="input" value={name} placeholder="e.g. SecOps Slack" onChange={(e) => setName(e.target.value)} />
              </Field>
            </div>
            {(kind === 'slack' || kind === 'teams' || kind === 'google_chat') && (
              <Field label="Incoming webhook URL" hint="https">
                <input className="input" value={cfg.webhookUrl ?? ''} placeholder="https://hooks.slack.com/services/…" onChange={(e) => setField('webhookUrl', e.target.value)} />
              </Field>
            )}
            {kind === 'webhook' && (
              <Field label="Endpoint URL" hint="https">
                <input className="input" value={cfg.url ?? ''} placeholder="https://…" onChange={(e) => setField('url', e.target.value)} />
              </Field>
            )}
            {kind === 'telegram' && (<>
              <Field label="Bot token"><input className="input" value={cfg.botToken ?? ''} placeholder="Bot token" onChange={(e) => setField('botToken', e.target.value)} /></Field>
              <Field label="Chat ID"><input className="input" value={cfg.chatId ?? ''} placeholder="Chat ID" onChange={(e) => setField('chatId', e.target.value)} /></Field>
            </>)}
            {kind === 'email' && (
              <Field label="Distribution address" hint="an org distribution list — not a personal mailbox">
                <input className="input" value={cfg.groupAddress ?? ''} placeholder="alerts@your-company.example" onChange={(e) => setField('groupAddress', e.target.value)} />
              </Field>
            )}
            <div className="form-actions">
              <Btn sm onClick={() => { reset(); setError(null); }}>Cancel</Btn>
              <Btn sm variant="primary" disabled={busy} onClick={() => void create()}>{busy ? 'Saving…' : 'Add channel'}</Btn>
            </div>
          </div>
        </Card>
      )}

      <ErrLine msg={error} />

      <div style={{ marginTop: 16 }}>
        {loading ? <p className="hint">Loading channels…</p>
          : channels && channels.length > 0 ? (
            <div className="doc-list">{channels.map((c) => (
              <div className="doc" key={c.id}>
                <div className="doc-ic">{CHANNEL_ICON[c.kind]}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="doc-n">{c.name} <Badge kind="out">{c.kind}</Badge></div>
                  <div className="doc-m">{c.classes && c.classes.length ? c.classes.join(', ') : 'all alert classes'} · {c.enabled ? 'enabled' : 'disabled'} · added {fmtDate(c.createdAt)}{testId === c.id && testMsg ? ` · ${testMsg}` : ''}</div>
                </div>
                {isManager && (
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    <Btn sm onClick={() => void test(c.id)}>Test</Btn>
                    <ConfirmDelete label="Delete" onConfirm={() => void remove(c.id)} />
                  </div>
                )}
              </div>
            ))}</div>
          ) : <p className="hint">No channels yet{isManager ? ' — add one so KEV/Art.14 alerts reach someone.' : '.'}</p>}
      </div>
    </Card>
  );
}

export function Settings() {
  const { session } = useSession();
  const [tab, setTab] = useState<TabKey>('org');
  const USE_FIXTURES = (import.meta.env.VITE_USE_FIXTURES as string | undefined) !== 'false';
  const { members } = useMembers(session.organizationId);
  const [dpm, setDpm] = useState<Member | null>(USE_FIXTURES ? (members.find((m) => m.id === ORG_PROFILE.defaultManagerRef) ?? null) : null);
  const [defaultMarkets, setDefaultMarkets] = useState<string[]>(USE_FIXTURES ? (ORG_PROFILE.defaultMarkets ?? []) : []);
  const [legalName, setLegalName] = useState<string>(USE_FIXTURES ? (ORG_PROFILE.legalName ?? '') : '');
  const [csirt, setCsirt] = useState<string>(USE_FIXTURES ? (ORG_PROFILE.csirt ?? 'CERT-Bund (DE)') : '');
  const [srpRegistered, setSrpRegistered] = useState<boolean>(USE_FIXTURES ? (ORG_PROFILE.srpRegistered ?? false) : false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [managerRef, setManagerRef] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (USE_FIXTURES) return;
    void apiFetch(session, '/api/org-profile').then(async (r) => {
      if (!r.ok) return;
      const p = (await r.json()) as OrgProfile;
      if (p.legalName) setLegalName(p.legalName);
      if (p.defaultMarkets) setDefaultMarkets(p.defaultMarkets);
      if (p.csirt) setCsirt(p.csirt);
      setSrpRegistered(p.srpRegistered ?? false);
      setManagerRef(p.defaultManagerRef ?? null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.organizationId]);
  // Resolve the stored default-manager ref against the roster once both arrive.
  useEffect(() => {
    if (USE_FIXTURES || !managerRef) return;
    const m = members.find((x) => x.id === managerRef);
    if (m) setDpm(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, managerRef]);
  async function saveOrg() {
    if (USE_FIXTURES) { setSaved(true); return; }
    setSaveError(null);
    try {
      const r = await apiFetch(session, '/api/org-profile', {
        method: 'PUT',
        body: JSON.stringify({ legalName, defaultManagerRef: dpm?.id, defaultMarkets, csirt, srpRegistered }),
      });
      setSaved(r.ok);
      // Never fail silently — an unsaved legal name later blocks the Q16 Sent gate (field 7).
      if (!r.ok) setSaveError(r.status === 403 ? 'Saving requires the Manager role.' : `Save failed (${r.status}) — the profile was NOT stored.`);
    } catch (e) {
      setSaved(false);
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  }

  // Role gating in-place (PAGES.md principle 5): manager-only areas simply don't
  // render for an engineer (server routes are admin/manager-gated regardless).
  const isManager = session.role === 'manager';
  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: 'org', label: 'Organization' }, { key: 'me', label: 'My profile' },
    ...(isManager
      ? ([{ key: 'users', label: 'Users & roles' }, { key: 'keys', label: 'API keys' }, { key: 'hooks', label: 'Webhooks' }] as Array<{ key: TabKey; label: string }>)
      : []),
    { key: 'alerts', label: 'Alerts' },
  ];

  return (
    <>
      <PageHead title="Settings" sub="Organisation, people, and integrations." />
      <div>
        <Tabs tabs={tabs} value={tab} onChange={setTab} />

        {tab === 'org' && (
          <div className="tabpane on">
            <Card style={{ padding: 20, maxWidth: 660 }}>
              <div className="section-head"><h3>Organisation &amp; ENISA SRP profile</h3></div>
              <div className="form" style={{ marginTop: 8 }}>
                <Field label="Legal entity"><input className="input" value={legalName} onChange={(e) => { setLegalName(e.target.value); setSaved(false); }} /></Field>
                <div className="grid2">
                  <Field label="SRP registration" hint="Enrolled in the ENISA Single Reporting Platform (EU Login) — needed before you can submit an Art.14 report.">
                    <label className="ck"><input type="checkbox" checked={srpRegistered} data-testid="srp-registered" onChange={(e) => { setSrpRegistered(e.target.checked); setSaved(false); }} /><span>Registered with the ENISA SRP</span></label>
                  </Field>
                  <Field label="Primary CSIRT" hint="The CSIRT of the Member State of your main establishment. Not established in the EU? Use the Member State of your authorised representative or importer.">
                    <CsirtCombo value={csirt} onChange={(v) => { setCsirt(v); setSaved(false); }} />
                  </Field>
                </div>
                <div className="form-actions"><Btn variant="primary" onClick={() => void saveOrg()}>{saved ? 'Saved ✓' : 'Save'}</Btn>{saveError && <span className="hint" data-testid="org-save-error" style={{ color: 'var(--critical)' }}>{saveError}</span>}</div>
              </div>
            </Card>

            {/* Non-blocking readiness guidance (ENISA-rules review): nothing here gates any flow. */}
            <Card style={{ padding: 20, maxWidth: 760, marginTop: 16 }} data-testid="cra-readiness">
              <div className="section-head">
                <h3>CRA readiness — before 11 September 2026</h3>
                <Badge kind="out">informative · nothing here blocks you</Badge>
              </div>
              <p className="hint" style={{ margin: '8px 0 10px' }}>
                The ENISA SRP launches 11 Sep 2026 — the same day the Art.14 clocks start. Submission is{' '}
                <b>manual web entry with EU Login</b> (no API, no file upload at launch), so do these while there is no deadline running:
              </p>
              <ul className="hint" style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8 }}>
                <li>
                  <b>EU Login accounts</b> for every incident manager who might submit — create them at{' '}
                  <a href="https://webgate.ec.europa.eu/cas/" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>webgate.ec.europa.eu/cas</a>.
                  The 24-hour window must never wait on account creation.
                </li>
                <li>
                  <b>Designated CSIRT identified</b> — set it in the profile above. It follows your main establishment;
                  not established in the EU → the Member State of your authorised representative or importer.
                </li>
                <li>
                  <b>SRP onboarding</b> — watch{' '}
                  <a href="https://www.enisa.europa.eu/topics/product-security-and-certification/single-reporting-platform-srp" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>ENISA&apos;s SRP page</a>{' '}
                  for the access manual and training material; complete registration and a dry run before launch.
                </li>
              </ul>
            </Card>

            <Card style={{ padding: 20, maxWidth: 760, marginTop: 16 }}>
              <div className="section-head"><h3>Defaults for new products</h3><span className="hint">Applied to every product you create — in the UI and via SBOM upload</span></div>
              <Field label="Default product manager" hint="the fallback owner for SBOM-created products and any product left without one (R-INC-01 / R-PROD-01)">
                <div style={{ maxWidth: 440 }}><UserCombo members={members} value={dpm} onPick={(m) => { setDpm(m); setSaved(false); }} /></div>
              </Field>
              <Field label="Default markets">
                <CountrySelector selected={defaultMarkets} editable onChange={(v) => { setDefaultMarkets(v); setSaved(false); }} />
              </Field>
              <div className="form-actions" style={{ marginTop: 16 }}><Btn variant="primary" onClick={() => void saveOrg()}>{saved ? 'Saved ✓' : 'Save defaults'}</Btn>{saveError && <span className="hint" style={{ color: 'var(--critical)' }}>{saveError}</span>}</div>
            </Card>
          </div>
        )}

        {tab === 'me' && (
          <div className="tabpane on">
            <Card style={{ padding: 20, maxWidth: 660 }}>
              <dl className="kv">
                <dt>Signed in as</dt><dd>{session.userName}</dd>
                <dt>Role</dt><dd>{session.role === 'manager' ? 'OEM Manager (Admin)' : 'OEM User'}</dd>
                <dt>Identity</dt><dd>Microsoft · <Badge kind="safe">no credentials stored here</Badge></dd>
              </dl>
            </Card>
          </div>
        )}

        {tab === 'users' && (
          <div className="tabpane on">
            <Card style={{ padding: 0 }}>
              <div className="tablewrap"><table>
                <thead><tr><th>Name</th><th>Role</th></tr></thead>
                <tbody>{members.filter((m) => m.id !== 'idp-default.im').map((m) => (
                  <tr key={m.id}><td><span className="owner"><span className="ava-sm">{m.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('')}</span>{m.name}</span></td><td className="mono">{m.role}</td></tr>
                ))}</tbody>
              </table></div>
            </Card>
          </div>
        )}

        {tab === 'keys' && (
          <div className="tabpane on"><KeysTab session={session} useFixtures={USE_FIXTURES} /></div>
        )}

        {tab === 'hooks' && (
          <div className="tabpane on"><HooksTab session={session} useFixtures={USE_FIXTURES} /></div>
        )}

        {tab === 'alerts' && (
          <div className="tabpane on"><AlertsTab session={session} useFixtures={USE_FIXTURES} isManager={isManager} /></div>
        )}
      </div>
    </>
  );
}
